import { Controller, useForm } from 'react-hook-form';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useDeleteContractFlow, useUpdateContract } from 'src/use-cases/serviceProvider/useServiceProvider';
import { format } from 'date-fns';
import { ContractDto, ContractFlowInfoViewDto, ContractUpsertRequestDto } from 'src/generated/services/TFinancialApi';
import { ReactNode, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Stack } from '@mui/material';
import IconInfo from 'src/assets/images/icons/info-circle-grey.svg';
import { FormContainer } from '../forms/FormContainer';
import { ModalConfirm } from '../../molecules/modal';

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormLabel-root': {
    fontSize: '16px',

    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiInputBase-input ': {
    fontSize: '16px',

    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
}));

interface IModalProps {
  contractToUpdate: ContractDto;
  shippingServiceId: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  contractFlow: ContractFlowInfoViewDto;
}

export const UpdateValidityPeriodModal = ({ contractToUpdate, shippingServiceId, open, setOpen, contractFlow }: IModalProps) => {
  const formContext = useForm({
    defaultValues: {
      shippingServiceId,
      startDate: new Date(contractToUpdate.startDate),
      endDate: new Date(contractToUpdate.endDate),
    },
  });

  const { control, handleSubmit, reset } = formContext;
  const { mutateAsync: updateValidityPeriod } = useUpdateContract();

  useEffect(() => {
    reset({
      shippingServiceId,
      startDate: new Date(contractToUpdate.startDate),
      endDate: new Date(contractToUpdate.endDate),
    });
  }, [contractToUpdate, reset, shippingServiceId]);

  const onSubmit = async (values) => {
    const reqBody: ContractUpsertRequestDto = {
      id: contractToUpdate.id,
      serviceProviderId: contractToUpdate.serviceProviderId!,
      startDate: format(values.startDate, 'yyyy-MM-dd'),
      endDate: format(values.endDate, 'yyyy-MM-dd'),
      contractFlowId: contractToUpdate.contractFlowId,
    };

    await updateValidityPeriod(reqBody);

    setOpen(false);
  };

  const { mutateAsync: deleteValidityPeriod } = useDeleteContractFlow();

  const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
  const [forceDeleteMsg, setForceDeleteMsg] = useState<ReactNode>(null);

  // Helper to count charges and return as array of JSX for bold lines
  const getChargeLines = () => {
    const charges = [
      { key: 'shipmentCharges', label: 'shipment charge(s)' },
      { key: 'containerCharges', label: 'container charge(s)' },
      { key: 'declarationCharges', label: 'declaration charge(s)' },
      { key: 'percentageCharges', label: 'percentage charge(s)' },
      { key: 'weightCharges', label: 'weight charge(s)' },
    ];
    return charges
      .map(({ key, label }) => {
        const arr = contractFlow[key as keyof ContractFlowInfoViewDto];
        if (Array.isArray(arr) && arr.length > 0) {
          return (
            <div key={key}>
              <b>
                * {arr.length} {label}
              </b>
            </div>
          );
        }
        return null;
      })
      .filter(Boolean);
  };

  const onDeleteValidityPeriod = async () => {
    const chargeLines = getChargeLines();
    if (chargeLines.length > 0) {
      setForceDeleteMsg(chargeLines);
      setShowForceDeleteModal(true);
      return;
    }
    await deleteValidityPeriod({
      id: contractToUpdate.contractFlowId,
      force: false,
    });
    setOpen(false);
  };

  const onForceDelete = async () => {
    await deleteValidityPeriod({
      id: contractToUpdate.contractFlowId,
      force: true,
    });
    setShowForceDeleteModal(false);
    setOpen(false);
  };

  return (
    <FormContainer formContext={formContext}>
      <ModalConfirm
        title='Update Validity Period'
        open={open}
        onAccept={() => {
          handleSubmit(onSubmit)();
        }}
        onCancel={() => {
          setOpen(false);
        }}
        width={766}
      >
        <StyledBox className='min-h-[350px]'>
          <Stack rowGap={3} sx={{ mt: '1rem' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                control={control}
                name='startDate'
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    label='Start Date'
                    value={field.value}
                    inputRef={field.ref}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    slotProps={{
                      popper: {
                        disablePortal: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name='endDate'
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    label='End Date'
                    value={field.value}
                    inputRef={field.ref}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    slotProps={{
                      popper: {
                        disablePortal: true,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            {contractToUpdate?.invoiceCount === 0 && (
              <div className=' h-[52px] rounded-[12px] bg-[#F5F6FA] flex items-center px-6'>
                <img src={IconInfo} alt='info' className='w-16 h-16 mx-8' />
                <span className='text-12 xl:text-14 text-neutral-70 mt-2'>No invoices are attached to the shipping service on this period</span>
                <button
                  type='button'
                  className='ml-auto text-12 xl:text-14 font-medium mx-8 -white bg-[#E72547] px-10 py-5 text-white rounded-[8px] hover:bg-[#E72547]/90'
                  onClick={onDeleteValidityPeriod}
                >
                  Delete Validity Period
                </button>
              </div>
            )}
          </Stack>
        </StyledBox>
      </ModalConfirm>
      <ModalConfirm
        open={showForceDeleteModal}
        title='Force Delete Validity Period'
        onAccept={onForceDelete}
        onCancel={() => setShowForceDeleteModal(false)}
        buttonAcceptLabel='Force Delete'
        buttonCancelLabel='Cancel'
        width={480}
      >
        <div>
          <div>That validity period is being linked to the following rate card charges:</div>
          {forceDeleteMsg}
          <div className='mt-8'>Do you want to continue force deleting this validity period?</div>
        </div>
      </ModalConfirm>
    </FormContainer>
  );
};
